/**
 * Contract SIGNING state — the one place that knows what a `public.contracts`
 * status means, who is still holding the pen, how long it has been sitting, and
 * whether that is long enough to be worth a nudge.
 *
 * ## Why this is a package
 *
 * "Is it signed yet, and who do I chase" was answered independently in five
 * places, each with its own copy of the status→label map and its own idea of
 * which statuses count as outstanding:
 *
 *   - `apps/hub/features/contracts/components/ContractsConfig.ts` (the table)
 *   - `apps/hub/features/contracts/components/tabs/ContractOverviewTab.tsx`
 *   - `apps/hub/lib/integrations/slack/notifications/deal-stage-changed/blocks.ts`
 *   - `apps/hub/features/contracts/components/ContractDetailConfig.ts`
 *   - `apps/ios/.../Contracts/Sales.swift` (`ContractSummary.isAwaitingSignature`)
 *
 * The iOS copy had already drifted: its awaiting set was
 * `sent | pending | awaiting_signature | out_for_signature`, three of which are
 * not statuses this system has ever written, and it omitted BOTH `viewed` and
 * `partially_signed` — the two states where a contract most needs chasing. A
 * contract a client had opened and not signed rendered as neither awaiting nor
 * signed, so the phone showed no nudge button on exactly the rows that wanted
 * one, and nothing anywhere was red.
 *
 * ## Pure by construction
 *
 * No DOM, no fetch, no database, no framework. Inputs are plain values and the
 * clock is an argument, so every rule here is testable and every surface gets
 * the same answer.
 *
 * ## NOT quotes
 *
 * A quote is a Drive PDF sent for rough approval; a contract is a
 * `public.contracts` row driven by the eSignatures lifecycle
 * (`QUOTES_VS_CONTRACTS`). Nothing here applies to quotes.
 */

// ===========================================================================
// Status vocabulary
// ===========================================================================

/**
 * Contract lifecycle statuses (`contracts.status`).
 *
 * A free-text column — no Postgres enum, no CHECK constraint — so a
 * hand-written, migrated, or future-provider row can hold anything at all.
 * Narrow at the boundary with `toContractSigningStatus` rather than asserting.
 */
export const CONTRACT_SIGNING_STATUSES = [
  "draft",
  "sent",
  "viewed",
  "partially_signed",
  "signed",
  "declined",
  "withdrawn",
  "error",
] as const;

export type ContractSigningStatus = (typeof CONTRACT_SIGNING_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set(CONTRACT_SIGNING_STATUSES);

/**
 * Narrow an unknown `contracts.status` to a known status, or `null`.
 *
 * `null` reads as "no known status" — never as an error and never as a default
 * of `draft`, which would tell a rep a live contract had never been sent.
 */
export function toContractSigningStatus(value: unknown): ContractSigningStatus | null {
  return typeof value === "string" && STATUS_SET.has(value)
    ? (value as ContractSigningStatus)
    : null;
}

/**
 * The statuses that mean a document is out with a customer and nobody has
 * finished signing it. This is THE definition of "awaiting signature", and it is
 * the filter behind Hub's saved view, the list route, and the phone screen.
 *
 * `draft` is excluded because nobody has it yet; `error` is excluded because a
 * send that failed is a different job (re-send it — do not nudge a signer who
 * was never emailed).
 */
export const AWAITING_SIGNATURE_STATUSES = ["sent", "viewed", "partially_signed"] as const;

export type AwaitingSignatureStatus = (typeof AWAITING_SIGNATURE_STATUSES)[number];

const AWAITING_SET: ReadonlySet<string> = new Set(AWAITING_SIGNATURE_STATUSES);

/** End states the lifecycle never leaves. */
export const TERMINAL_SIGNING_STATUSES = ["signed", "declined", "withdrawn"] as const;

const TERMINAL_SET: ReadonlySet<string> = new Set(TERMINAL_SIGNING_STATUSES);

/** Out with a customer, unsigned. An unknown status is NOT awaiting. */
export function isAwaitingSignature(status: string | null | undefined): boolean {
  return typeof status === "string" && AWAITING_SET.has(status);
}

/** Fully executed. */
export function isSignedStatus(status: string | null | undefined): boolean {
  return status === "signed";
}

/** Signed, declined or withdrawn — the lifecycle is over either way. */
export function isTerminalStatus(status: string | null | undefined): boolean {
  return typeof status === "string" && TERMINAL_SET.has(status);
}

// ===========================================================================
// Presentation
// ===========================================================================

/**
 * A semantic colour role, not a colour.
 *
 * Every surface renders WTC's own palette (`main-00`): Hub web maps a tone to a
 * Tailwind pair, Slack to an emoji, iOS to a `Brand` colour. Shipping a hex or a
 * class name from here would put one app's design system in every other app's
 * bundle.
 */
export type ContractStatusTone = "neutral" | "info" | "progress" | "positive" | "negative";

export interface ContractStatusPresentation {
  /** Sentence-case human label. */
  readonly label: string;
  readonly tone: ContractStatusTone;
  /** Convenience mirror of `isAwaitingSignature`, for table-driven code. */
  readonly awaiting: boolean;
}

export const CONTRACT_STATUS_PRESENTATION: Readonly<
  Record<ContractSigningStatus, ContractStatusPresentation>
> = {
  draft: { label: "Draft", tone: "neutral", awaiting: false },
  sent: { label: "Sent", tone: "info", awaiting: true },
  viewed: { label: "Viewed", tone: "info", awaiting: true },
  partially_signed: { label: "Partially signed", tone: "progress", awaiting: true },
  signed: { label: "Signed", tone: "positive", awaiting: false },
  declined: { label: "Declined", tone: "negative", awaiting: false },
  withdrawn: { label: "Withdrawn", tone: "negative", awaiting: false },
  error: { label: "Error", tone: "negative", awaiting: false },
};

/**
 * Prettify a status we do not recognise: `some_other_state` becomes
 * `Some other state`. Only ever the fallback — the column is free text, and a
 * raw snake_case token in a badge is worse than a guess at capitalisation.
 */
function prettifyUnknownStatus(raw: string): string {
  const cleaned = raw.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (cleaned.length === 0) return "Unknown";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
}

/** The label to show for any status value, known or not. */
export function contractStatusLabel(status: string | null | undefined): string {
  const known = toContractSigningStatus(status);
  if (known) return CONTRACT_STATUS_PRESENTATION[known].label;
  return typeof status === "string" && status.trim().length > 0
    ? prettifyUnknownStatus(status)
    : "Unknown";
}

/** The tone to render for any status value. An unknown status is neutral. */
export function contractStatusTone(status: string | null | undefined): ContractStatusTone {
  const known = toContractSigningStatus(status);
  return known ? CONTRACT_STATUS_PRESENTATION[known].tone : "neutral";
}

// ===========================================================================
// The row this module reasons about
// ===========================================================================

/**
 * The fields of a `public.contracts` row that signing state depends on.
 *
 * Deliberately a narrow structural type rather than the generated table Row:
 * this package is imported by Hub (which has the generated types), by the list
 * API route (which selects a subset), and mirrored by iOS — none of which should
 * have to agree on anything beyond these names.
 *
 * All ISO-8601 instants, all nullable, because every one of them is.
 */
export interface ContractSigningRow {
  readonly status?: string | null;
  /** When the agreement went out. */
  readonly sentAt?: string | null;
  /** The most recent provider lifecycle event (opened, partially signed, …). */
  readonly lastEventAt?: string | null;
  readonly signedAt?: string | null;
  readonly declineReason?: string | null;
  readonly recipientName?: string | null;
  readonly recipientEmail?: string | null;
  readonly documentName?: string | null;
  readonly templateName?: string | null;
  readonly companyName?: string | null;
}

// ===========================================================================
// Staleness / nudge-worthiness
// ===========================================================================

/**
 * How long a contract may sit since its last sign of life before a nudge is the
 * right move.
 *
 * Four days is deliberately not "tomorrow": an agreement usually needs a signer
 * to find a moment, and a reminder the morning after sending reads as pressure
 * rather than service. It is also deliberately not two weeks — by then the deal
 * has cooled and the nudge is an apology.
 */
export const CONTRACT_NUDGE_AFTER_DAYS = 4;

/** Past this, the silence is the story, not the wait. */
export const CONTRACT_STALE_AFTER_DAYS = 10;

/** Past this, assume it is dead unless somebody does something different. */
export const CONTRACT_STALLED_AFTER_DAYS = 21;

/**
 * How badly a contract needs chasing.
 *
 * `fresh` — sent recently; leave it alone.
 * `warm` — old enough that a reminder is service, not pressure.
 * `stale` — the reminder should come with a phone call.
 * `stalled` — stop nudging and find out what actually happened.
 */
export type ContractChaseTier = "fresh" | "warm" | "stale" | "stalled";

/** Rank for sorting. Higher means chase sooner. */
const TIER_RANK: Readonly<Record<ContractChaseTier, number>> = {
  stalled: 3,
  stale: 2,
  warm: 1,
  fresh: 0,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole days between an instant and `now`, or `null` when the input is missing
 * or unparseable. A negative difference (clock skew, a future `sent_at`) clamps
 * to 0 — "sent -1 days ago" is never the honest answer.
 */
export function wholeDaysBetween(
  isoEarlier: string | null | undefined,
  now: Date,
): number | null {
  if (typeof isoEarlier !== "string" || isoEarlier.trim().length === 0) return null;
  const then = Date.parse(isoEarlier);
  if (Number.isNaN(then)) return null;
  const diff = now.getTime() - then;
  return diff <= 0 ? 0 : Math.floor(diff / MS_PER_DAY);
}

/**
 * The tier for a given number of days of silence.
 *
 * Exported because a caller sometimes already has the number (a digest that
 * grouped by age) and should not have to rebuild a row to ask.
 */
export function chaseTierForDays(daysSinceLastEvent: number | null): ContractChaseTier {
  if (daysSinceLastEvent === null) return "fresh";
  if (daysSinceLastEvent >= CONTRACT_STALLED_AFTER_DAYS) return "stalled";
  if (daysSinceLastEvent >= CONTRACT_STALE_AFTER_DAYS) return "stale";
  if (daysSinceLastEvent >= CONTRACT_NUDGE_AFTER_DAYS) return "warm";
  return "fresh";
}

// ===========================================================================
// Who has not signed
// ===========================================================================

function trimmed(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const out = value.trim();
  return out.length > 0 ? out : null;
}

/**
 * Who is still holding the pen, in words a rep can act on.
 *
 * ## What this can and cannot know
 *
 * `public.contracts` stores ONE recipient (`recipient_name` / `recipient_email`)
 * and no signer roster — the roster lives at eSignatures and is fetched live by
 * the resend path (`resendContractToSigners` GETs the contract to resolve signer
 * ids precisely because the row does not have them). So:
 *
 *  - `sent` / `viewed` — the recipient we mailed is the person who has not signed.
 *  - `partially_signed` — at least one signer HAS signed and at least one has
 *    not, and the row cannot say which. "At least one signer still has to sign"
 *    is true; naming the recipient would be a coin-flip that could name the
 *    person who already signed.
 *
 * Returns `null` when nobody is outstanding (signed, declined, withdrawn,
 * draft), so the caller renders nothing rather than a hedge.
 */
export function describeOutstandingSigners(row: ContractSigningRow): string | null {
  if (!isAwaitingSignature(row.status)) return null;

  const name = trimmed(row.recipientName);
  const email = trimmed(row.recipientEmail);

  if (row.status === "partially_signed") {
    // Deliberately unnamed — see the doc comment.
    return "At least one signer still has to sign";
  }

  if (name && email) return `${name} <${email}>`;
  if (name) return name;
  if (email) return email;
  return "The signer we emailed";
}

/**
 * The best display name for a contract.
 *
 * `document_name` is the human title, `template_name` the fallback ("Schooley
 * Mitchell Cost Savings Agreement"); neither is guaranteed. Never returns an
 * empty string.
 */
export function contractDisplayTitle(row: ContractSigningRow): string {
  return trimmed(row.documentName) ?? trimmed(row.templateName) ?? "Contract";
}

// ===========================================================================
// The derived state every surface renders
// ===========================================================================

export interface ContractSigningState {
  /** The narrowed status, or `null` for a value outside the vocabulary. */
  readonly status: ContractSigningStatus | null;
  /** Always renderable, even for an unknown status. */
  readonly statusLabel: string;
  readonly tone: ContractStatusTone;

  readonly isAwaitingSignature: boolean;
  readonly isSigned: boolean;
  readonly isDeclined: boolean;
  readonly isTerminal: boolean;

  /** Whole days since it went out. `null` when `sentAt` is missing/unparseable. */
  readonly daysSinceSent: number | null;
  /** Whole days since ANY lifecycle event (falls back to `sentAt`). */
  readonly daysSinceLastEvent: number | null;

  readonly tier: ContractChaseTier;
  /** Outstanding AND quiet long enough that a reminder is the right move. */
  readonly shouldNudge: boolean;

  /** "Sent 9 days ago", or `null` when it is not waiting on anyone. */
  readonly waitingLabel: string | null;
  /** @see describeOutstandingSigners */
  readonly outstandingSigners: string | null;
  /** One sentence saying why this row is in a chase list, or `null`. */
  readonly chaseReason: string | null;

  readonly title: string;
}

/** "Sent today" / "Sent yesterday" / "Sent 9 days ago". */
function describeWait(daysSinceSent: number | null): string {
  if (daysSinceSent === null) return "Waiting — no send date recorded";
  if (daysSinceSent === 0) return "Sent today";
  if (daysSinceSent === 1) return "Sent yesterday";
  return `Sent ${daysSinceSent} days ago`;
}

/**
 * Why this row is worth a rep's attention, in one sentence.
 *
 * The status matters as much as the age: `viewed` means they opened it and
 * closed it again, which is a different conversation from `sent` (which may
 * still be sitting unread in a spam folder).
 */
function describeChaseReason(
  status: string | null | undefined,
  tier: ContractChaseTier,
  days: number | null,
): string {
  const quiet = days === null ? "a while" : days === 1 ? "1 day" : `${days} days`;

  if (tier === "stalled") {
    return `No movement in ${quiet}. A reminder has probably run out of road — call instead.`;
  }
  if (tier === "stale") {
    return status === "viewed"
      ? `Opened and left unsigned for ${quiet}. Worth asking what is blocking it.`
      : `Quiet for ${quiet}. Send the reminder, then follow it with a call.`;
  }
  if (tier === "warm") {
    if (status === "viewed") return `Opened ${quiet} ago and not signed. A reminder is fair now.`;
    if (status === "partially_signed") {
      return `Partly signed, quiet for ${quiet}. The remaining signer needs a reminder.`;
    }
    return `Out for ${quiet} with no response. A reminder is fair now.`;
  }
  return status === "partially_signed"
    ? "Partly signed — the remaining signer has not finished yet."
    : "Recently sent. Give them a little longer.";
}

/**
 * Derive everything a "chase the signature" surface needs from one row.
 *
 * `now` is an argument so this stays a pure function — the tests pin real dates
 * rather than sleeping, and the SERVER can compute the same state it hands a
 * phone. That is the point of the split: the iOS mirror carries the vocabulary,
 * not the arithmetic.
 */
export function summarizeContractSigning(
  row: ContractSigningRow,
  now: Date = new Date(),
): ContractSigningState {
  const status = toContractSigningStatus(row.status);
  const awaiting = isAwaitingSignature(row.status);

  const daysSinceSent = wholeDaysBetween(row.sentAt, now);
  const daysSinceLastEvent = wholeDaysBetween(row.lastEventAt, now) ?? daysSinceSent;

  // Only an outstanding contract has a chase tier. A signed contract untouched
  // for a year is not "stalled" — it is done.
  const tier = awaiting ? chaseTierForDays(daysSinceLastEvent) : "fresh";
  const shouldNudge = awaiting && tier !== "fresh";

  return {
    status,
    statusLabel: contractStatusLabel(row.status),
    tone: contractStatusTone(row.status),
    isAwaitingSignature: awaiting,
    isSigned: isSignedStatus(row.status),
    isDeclined: row.status === "declined",
    isTerminal: isTerminalStatus(row.status),
    daysSinceSent,
    daysSinceLastEvent,
    tier,
    shouldNudge,
    waitingLabel: awaiting ? describeWait(daysSinceSent) : null,
    outstandingSigners: describeOutstandingSigners(row),
    chaseReason: awaiting ? describeChaseReason(row.status, tier, daysSinceLastEvent) : null,
    title: contractDisplayTitle(row),
  };
}

// ===========================================================================
// Ordering
// ===========================================================================

/**
 * Chase order: the row a rep should deal with first, first.
 *
 * Tier before age, so a 30-day silence outranks a 5-day one but a `stale`
 * contract never sinks below a `fresh` one that happens to be older on paper
 * (which happens whenever a fresh lifecycle event resets the clock). Ties break
 * on days waiting, then on title, so the order is stable across reloads instead
 * of shuffling on every fetch.
 */
export function compareByChaseUrgency(
  a: ContractSigningState,
  b: ContractSigningState,
): number {
  const tierDelta = TIER_RANK[b.tier] - TIER_RANK[a.tier];
  if (tierDelta !== 0) return tierDelta;

  const aDays = a.daysSinceLastEvent ?? -1;
  const bDays = b.daysSinceLastEvent ?? -1;
  if (aDays !== bDays) return bDays - aDays;

  return a.title.localeCompare(b.title);
}
